import { useEffect, useMemo, useRef, useState } from 'react'
import { PRIVACY_POLICY_URL } from '../config'
import { digitsOnly, formatDanishPhone, validateDanishPhone } from '../lib/phone'
import type { StepInputProps } from './StepInput'

export function PhoneInput({ state, onSubmit }: StepInputProps) {
  const initialName = [state.firstName, state.lastName].filter(Boolean).join(' ')
  const [name, setName] = useState(initialName)
  const [raw, setRaw] = useState(state.phone ?? '')
  const [touched, setTouched] = useState(false)
  const nameRef = useRef<HTMLInputElement>(null)
  useEffect(() => { nameRef.current?.focus() }, [])

  const digits = useMemo(() => digitsOnly(raw), [raw])
  const formatted = useMemo(() => formatDanishPhone(digits), [digits])
  const validation = useMemo(() => validateDanishPhone(digits), [digits])

  const nameValid = name.trim().length > 0
  const allValid = nameValid && validation.valid

  // Show error only after user has typed all 8 digits — don't nag mid-typing.
  const showError = touched && digits.length === 8 && !validation.valid && !!validation.msg

  const submit = () => {
    if (!validation.valid) { setTouched(true); return }
    if (!nameValid) return
    // Split på første mellemrum → firstName / lastName. Backend kræver
    // last_name NOT NULL på /api/lead, så vi bruger '-' som placeholder hvis
    // brugeren kun skrev fornavn (samme pattern som /api/calc-lead bruger).
    const trimmed = name.trim()
    const spaceIdx = trimmed.indexOf(' ')
    const firstName = spaceIdx === -1 ? trimmed : trimmed.slice(0, spaceIdx).trim()
    const lastName = spaceIdx === -1 ? '-' : trimmed.slice(spaceIdx + 1).trim() || '-'
    onSubmit({
      firstName,
      lastName,
      phone: formatted,
      consentRequired: true,
      consentMarketing: false,
    })
  }

  return (
    <div className="input-dock phone-dock">
      <div className="phone-trust">
        <span className="phone-trust-item">✓ Gratis</span>
        <span className="phone-trust-dot" aria-hidden="true">·</span>
        <span className="phone-trust-item">✓ Uforpligtende</span>
        <span className="phone-trust-dot" aria-hidden="true">·</span>
        <span className="phone-trust-item">★ 4,9 på Trustpilot</span>
      </div>

      <input
        ref={nameRef}
        type="text"
        className="input-field"
        placeholder="Navn"
        autoComplete="name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && submit()}
      />

      <div className={`phone-field ${showError ? 'phone-field-error' : ''}`}>
        <span className="phone-prefix" aria-hidden="true">🇩🇰 +45</span>
        <input
          type="tel"
          className="phone-input"
          placeholder="12 34 56 78"
          inputMode="numeric"
          autoComplete="tel-national"
          maxLength={11}
          value={formatted}
          onChange={(e) => { setRaw(e.target.value); if (touched) setTouched(false) }}
          onBlur={() => setTouched(true)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          aria-invalid={showError}
          aria-describedby={showError ? 'phone-error' : undefined}
        />
      </div>

      {showError && (
        <p className="phone-error" id="phone-error" role="alert">{validation.msg}</p>
      )}

      <button
        className="btn-send btn-send-cta full"
        onClick={submit}
        disabled={!allValid}
      >
        Få mit tilbud →
      </button>

      <p className="consent-inline">
        Ved at indsende dit telefonnummer accepterer du{' '}
        <a href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer noopener">privatlivspolitikken</a>{' '}
        og at blive kontaktet af en forsikringsrådgiver med et uforpligtende tilbud.
      </p>
    </div>
  )
}
